import { getRegionCode } from '~/Utils/regionUtils';

import { SLOT_DETAILS_MAX_LENGTH } from './constants';
import type { CapacityAgreementDisplayFieldsType, CapacitySlotType } from './types';

const CAPACITY_AGREEMENT_BY_REGION = {
  US: {
    full: 'capacity agreement',
    fullCap: 'Capacity Agreement',
    short: 'agreement',
    shortCap: 'Agreement',
  },
  UK: {
    full: 'capacity agreement',
    fullCap: 'Capacity Agreement',
    short: 'agreement',
    shortCap: 'Agreement',
  },
  AU: {
    full: 'binder',
    fullCap: 'Binder',
    short: 'binder',
    shortCap: 'Binder',
  },
  EU: {
    full: 'capacity agreement',
    fullCap: 'Capacity Agreement',
    short: 'agreement',
    shortCap: 'Agreement',
  },
  default: {
    full: 'capacity agreement',
    fullCap: 'Capacity Agreement',
    short: 'agreement',
    shortCap: 'Agreement',
  },
} as const satisfies Record<string, { full: string; fullCap: string; short: string; shortCap: string }>;

const SLOT_BY_REGION = {
  US: {
    full: 'capacity slot',
    fullCap: 'Capacity Slot',
    short: 'slot',
    shortCap: 'Slot',
  },
  UK: {
    full: 'capacity slot',
    fullCap: 'Capacity Slot',
    short: 'slot',
    shortCap: 'Slot',
  },
  AU: {
    full: 'section',
    fullCap: 'Section',
    short: 'section',
    shortCap: 'Section',
  },
  EU: {
    full: 'capacity slot',
    fullCap: 'Capacity Slot',
    short: 'slot',
    shortCap: 'Slot',
  },
  default: {
    full: 'capacity slot',
    fullCap: 'Capacity Slot',
    short: 'slot',
    shortCap: 'Slot',
  },
} as const satisfies Record<string, { full: string; fullCap: string; short: string; shortCap: string }>;

const getRegionCodeOrDefault = () => getRegionCode() || 'default';

const capacityAgreementByRegion = () => CAPACITY_AGREEMENT_BY_REGION[getRegionCodeOrDefault()].full;
const capacityAgreementByRegionCapitalized = () => CAPACITY_AGREEMENT_BY_REGION[getRegionCodeOrDefault()].fullCap;
const capacityAgreementShortByRegionCapitalized = () => CAPACITY_AGREEMENT_BY_REGION[getRegionCodeOrDefault()].shortCap;
const capacitySlotByRegionCapitalized = () => SLOT_BY_REGION[getRegionCodeOrDefault()].fullCap;
const capacitySlotShortByRegion = () => SLOT_BY_REGION[getRegionCodeOrDefault()].short;
const capacitySlotShortByRegionCapitalized = () => SLOT_BY_REGION[getRegionCodeOrDefault()].shortCap;

const CAPACITY_AGREEMENT_DISPLAY_FIELDS_LABELS = {
  agreement_number: () => `${capacityAgreementShortByRegionCapitalized()} Number`,
  umr: () => 'UMR',
  lloyds_risk_code: () => 'Lloyd’s Risk Code',
  carrier_name: () => 'Carrier Name',
  effective_date: () => 'Effective Date',
  expiration_date: () => 'Expiration Date',
} as const satisfies Record<CapacityAgreementDisplayFieldsType, () => string>;

const CAPACITY_SLOTS_LABELS = {
  slot_name: 'Name',
  slot_number: 'Number',
  details: 'Details',
  external_id: 'External ID',
} as const satisfies Record<Exclude<keyof CapacitySlotType, 'id'>, string>;

export const TEXTS = {
  sectionTitle: () => `All ${capacityAgreementByRegionCapitalized()}s`,
  noCapacityAgreements: () => `No ${capacityAgreementByRegion()}s added yet`,
  add: 'ADD',
  edit: 'Edit',
  missingField: '-',
  capacityFieldLabels: CAPACITY_AGREEMENT_DISPLAY_FIELDS_LABELS,
  slotsFieldLabels: CAPACITY_SLOTS_LABELS,
  slotsTitle: () => `${capacitySlotShortByRegionCapitalized()}s`,
  noSlots: () => `No ${capacitySlotShortByRegion()}s added yet`,
  actions: 'Actions',
  addDialogTitle: () => `Add a ${capacityAgreementByRegionCapitalized()}`,
  updateDialogTitle: () => `Update the ${capacityAgreementByRegionCapitalized()}`,
  editSlotDialogTitle: () => `Edit ${capacitySlotByRegionCapitalized()}`,
  required: 'Required',
  expirationDateMustBeAfterEffectiveDate: 'Expiration date must be after effective date',
  lloydsRiskCodeMaxLength: 'Should consist of 1 or 2 characters',
  slotDetailsMaxLength: `Should be at most ${SLOT_DETAILS_MAX_LENGTH} characters`,
  submit: 'Submit',
  cancel: 'Cancel',
  addSlot: () => `Add ${capacitySlotShortByRegionCapitalized()}`,
  removeSlot: () => `Remove ${capacitySlotShortByRegionCapitalized()}`,
} as const;
